/**
 * @fileoverview The one read of the See them page (E19): every crop that can go in now at the garden's place that the
 * garden has none of, with the search and the Add sheet, all decided by the server.
 *
 * Where: WhatToPlantBrowsePage.
 * API: GET /plants/what-to-plant/browse?lang= (api/whatToPlant).
 * Caching: keyed like the What to plant list (the garden, its own day, the language), so a new day or language reads
 * again. An add marks it stale without reading it again on the page (useAddCrop), so the row just added stays where it
 * is until she leaves. Not read until the garden's place (and so its day) is known: the first read at a place is slow.
 *
 * @module useWhatToPlantBrowse
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getWhatToPlantBrowse, type WhatToPlantBrowse } from '@/shared/api/whatToPlant';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';

/** The first word of the See them page's React Query key. @kept K1 */
export const WHAT_TO_PLANT_BROWSE_QUERY_KEY = 'what-to-plant-browse';

/** What the page is handed. */
export interface WhatToPlantBrowseReading {
  /** The server's answer, or undefined until it is in. */
  data: WhatToPlantBrowse | undefined;
  /** True while the first read is on its way. */
  isLoading: boolean;
  /** The failure, or null. */
  error: unknown;
}

/**
 * Reads the See them page for the garden being tended.
 *
 * @returns The answer, whether it is loading, and any failure.
 */
export function useWhatToPlantBrowse(): WhatToPlantBrowseReading {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const { date, known } = useGardenPostcode();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const { data, isLoading, error } = useQuery({
    queryKey: [WHAT_TO_PLANT_BROWSE_QUERY_KEY, gardenId, date, language],
    queryFn: () => getWhatToPlantBrowse(language),
    enabled: Boolean(mysqlUser) && known,
    staleTime: QUERY_STALE_TIME_MS,
  });
  return { data, isLoading: isLoading || !known, error: error ?? null };
}
